import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild
} from '@angular/core';

@Component({
  selector: 'app-number-highlighter',
  templateUrl: './number-highlighter.component.html',
  styleUrls: ['./number-highlighter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NumberHighlighterComponent implements OnChanges {
  @Input({ required: true }) public value: string;
  @Input({ required: true }) public startAfterDecimal: boolean;
  @Input({ required: true }) public indicesToHighlight: number[] = [];
  @Input() public contentEditable = false;
  @Input() public pending = false;
  @Output() onBlur = new EventEmitter();
  @Output() onFocus = new EventEmitter();
  @Output() onValueChange = new EventEmitter<string>(null);
  private valueBeingDisplayed: string;

  @ViewChild('numberHighlighter')
  private numberHighlighter: ElementRef;
  private renderer2 = inject(Renderer2);
  private isFocused = false;

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.value?.currentValue !== undefined &&
      this.contentEditable &&
      !this.isFocused
    ) {
      this.changeInValue(changes.value.currentValue, false, true);
    }

    if (
      changes.indicesToHighlight?.currentValue !== undefined &&
      this.contentEditable &&
      !this.isFocused
    ) {
      this.changeInValue(this.value, false, true);
    }

    if (changes.contentEditable?.currentValue && this.value) {
      setTimeout(_ => {
        this.displayValue(this.value);
      }, 0);
    }
  }

  public changeInValue(
    value: string,
    manualChange: boolean,
    wipeValue = true
  ): void {
    if (!this.numberHighlighter || !this.contentEditable) {
      return;
    }
    if (wipeValue) {
      this.renderer2.setProperty(
        this.numberHighlighter.nativeElement,
        'textContent',
        ''
      );
    }

    if (value == null) {
      return;
    }
    this.valueBeingDisplayed = value.toString();
    if (value) {
      this.displayValue();
    }

    this.onValueChange.emit(value);
    if (manualChange) {
      this.blur();
    }
  }

  private displayValue(value?: string): void {
    const chars = (value || this.valueBeingDisplayed).split('.');
    const span = this.renderer2.createElement('span');
    const text = this.renderer2.createText(chars[0]);
    this.renderer2.appendChild(span, text);
    this.renderer2.appendChild(this.numberHighlighter.nativeElement, span);

    if (chars[1]) {
      const span = this.renderer2.createElement('span');
      const text = this.renderer2.createText('.');
      this.renderer2.appendChild(span, text);
      this.renderer2.appendChild(this.numberHighlighter.nativeElement, span);
      chars[1].split('').forEach((char, index) => {
        const span = this.renderer2.createElement('span');
        const text = this.renderer2.createText(char);
        if (this.indicesToHighlight.includes(index)) {
          this.renderer2.addClass(span, 'highlight-text');
        }
        this.renderer2.appendChild(span, text);
        this.renderer2.appendChild(this.numberHighlighter.nativeElement, span);
      });
    }
  }

  public focus(): void {
    const children = (this.numberHighlighter.nativeElement as HTMLElement)
      .childNodes;
    children.forEach(child => {
      this.renderer2.removeClass(child, 'highlight-text');
    });
    this.isFocused = true;
    this.onFocus.emit();
  }

  public blur(): void {
    if (this.contentEditable && this.valueBeingDisplayed !== this.value) {
      this.changeInValue('', true, true);
    }
    this.renderer2.setProperty(
      this.numberHighlighter.nativeElement,
      'scrollLeft',
      0
    );
    this.isFocused = false;
    this.onBlur.emit();
  }

  public onInput(value: string): void {
    this.onValueChange.emit(value);
  }
}
